export const SLOT_MINUTES = 20;
export const SLOT_MARKS = [20, 40];
export const SLOT_STARTS = [0, ...SLOT_MARKS];
export const DEFAULT_START_HOUR = 8;
export const DEFAULT_END_HOUR = 20;

const toBackendWeekday = (jsWeekday) => (jsWeekday + 6) % 7;

const parseClock = (value) => {
  const match = String(value || "").match(/(\d{1,2}):(\d{2})/);
  if (!match) return null;

  return {
    hour: Number(match[1]),
    minute: Number(match[2]),
  };
};

export const padTime = (value) => String(value).padStart(2, "0");

export const getExecutionMinutes = (order) => {
  const value = order?.execution_time;
  if (value == null || value === "") return SLOT_MINUTES;

  if (typeof value === "number") {
    return value > 0 ? value : SLOT_MINUTES;
  }

  const parts = String(value).split(":");
  if (parts.length < 2) {
    const minutes = Number(parts[0]);
    return minutes > 0 ? minutes : SLOT_MINUTES;
  }

  const hours = Number(parts[0]) || 0;
  const minutes = Number(parts[1]) || 0;
  const total = hours * 60 + minutes;

  return total > 0 ? total : SLOT_MINUTES;
};

export const getPlannedMinutes = (order) => {
  const match = String(order?.planned_at || "").match(/(?:T|\s)(\d{2}):(\d{2})/);
  if (!match) return null;

  return Number(match[1]) * 60 + Number(match[2]);
};

const snapToSlot = (minutes) => Math.round(minutes / SLOT_MINUTES) * SLOT_MINUTES;

const ceilToSlotMinutes = (minutes) =>
  Math.max(SLOT_MINUTES, Math.ceil(minutes / SLOT_MINUTES) * SLOT_MINUTES);

const getRowStartMinutes = (row) => row.hour * 60 + (row.collapsed ? 0 : row.minute);

export const getTodayWorkDay = (workDays = [], date = new Date()) => {
  if (!Array.isArray(workDays)) return null;

  const weekday = toBackendWeekday(date.getDay());
  return workDays.find((item) => item.weekday === weekday) || null;
};

export const getHourRange = (workDay, orders = []) => {
  let startHour = DEFAULT_START_HOUR;
  let endHour = DEFAULT_END_HOUR;

  const opened = parseClock(workDay?.opened_at);
  const closed = parseClock(workDay?.closed_at);

  if (opened) {
    startHour = opened.hour;
  }

  if (closed) {
    endHour = closed.minute > 0 ? closed.hour : closed.hour - 1;
  }

  orders.forEach((order) => {
    const start = getPlannedMinutes(order);
    if (start == null) return;

    const snappedStart = snapToSlot(start);
    const end = snappedStart + ceilToSlotMinutes(getExecutionMinutes(order));
    startHour = Math.min(startHour, Math.floor(snappedStart / 60));
    endHour = Math.max(endHour, Math.floor((end - 1) / 60));
  });

  startHour = Math.min(23, Math.max(0, startHour));
  endHour = Math.min(23, Math.max(startHour, endHour));

  return { startHour, endHour };
};

const collectOrderSlots = (order, onSlot) => {
  const start = getPlannedMinutes(order);
  if (start == null) return;

  const snappedStart = snapToSlot(start);
  const end = snappedStart + ceilToSlotMinutes(getExecutionMinutes(order));

  for (let minute = snappedStart; minute < end; minute += SLOT_MINUTES) {
    onSlot(minute);
  }
};

export const getOccupiedHours = (orders = []) => {
  const hours = new Set();

  orders.forEach((order) => {
    collectOrderSlots(order, (minute) => {
      hours.add(Math.floor(minute / 60));
    });
  });

  return hours;
};

export const getOccupiedSlots = (orders = [], viewStart) => {
  const slots = new Set();
  const hasViewStart = Number.isFinite(viewStart);

  orders.forEach((order) => {
    const planned = getPlannedMinutes(order);
    if (planned == null) return;

    const start = snapToSlot(planned);
    const end = start + ceilToSlotMinutes(getExecutionMinutes(order));

    if (hasViewStart && end <= viewStart) {
      slots.add(viewStart);
      return;
    }

    const from = hasViewStart ? Math.max(start, viewStart) : start;
    for (let minute = from; minute < end; minute += SLOT_MINUTES) {
      slots.add(minute);
    }
  });

  return slots;
};

export const getNowSlot = (currentMinutes) =>
  Math.floor(
    (Number.isFinite(currentMinutes) ? currentMinutes : getCurrentMinutes()) /
      SLOT_MINUTES,
  ) * SLOT_MINUTES;

export const getCurrentHour = (time) => {
  const minutes = getCurrentMinutes(time);
  return Math.floor(minutes / 60);
};

export const getCurrentMinutes = (time) => {
  const match = String(time || "").match(/^(\d{1,2}):(\d{2})/);
  if (match) return Number(match[1]) * 60 + Number(match[2]);

  const now = new Date();
  return now.getHours() * 60 + now.getMinutes();
};

export const getViewStartMinutes = (orders = [], currentMinutes) => {
  const nowMinutes = Number.isFinite(currentMinutes)
    ? currentMinutes
    : getCurrentMinutes();
  const nowSlot = Math.floor(nowMinutes / SLOT_MINUTES) * SLOT_MINUTES;

  return orders.reduce((viewStart, order) => {
    const planned = getPlannedMinutes(order);
    if (planned == null) return viewStart;

    const start = snapToSlot(planned);
    return start < viewStart ? start : viewStart;
  }, nowSlot);
};

export const buildHourRows = ({
  startHour,
  endHour,
  occupiedSlots,
  currentHour,
  currentMinutes,
  viewStartMinutes,
}) => {
  const hours = [];
  const rows = [];
  const nowMinutes = Number.isFinite(currentMinutes)
    ? currentMinutes
    : Number.isFinite(currentHour)
      ? currentHour * 60
      : getCurrentMinutes();
  const nowSlot = Math.floor(nowMinutes / SLOT_MINUTES) * SLOT_MINUTES;
  const viewStart = Number.isFinite(viewStartMinutes)
    ? viewStartMinutes
    : nowSlot;
  const slots = occupiedSlots || new Set();

  for (let hour = startHour; hour <= endHour; hour += 1) {
    const hourStart = hour * 60;
    const visibleSlots = SLOT_STARTS.filter((minute) => {
      const slot = hourStart + minute;
      if (slots.has(slot)) return true;
      return slot >= viewStart;
    });
    const hasOrders = visibleSlots.some((minute) => slots.has(hour * 60 + minute));

    if (!visibleSlots.length) continue;

    const startRow = rows.length;

    if (hasOrders) {
      visibleSlots.forEach((minute, index) => {
        rows.push({
          hour,
          minute,
          collapsed: false,
          isHourEnd: index === visibleSlots.length - 1,
        });
      });

      hours.push({
        hour,
        label: padTime(hour),
        hasOrders,
        startRow,
        endRow: rows.length,
        marks: visibleSlots.slice(1).map((minute, index) => ({
          minute,
          offset: index + 1,
        })),
      });
      continue;
    }

    rows.push({ hour, minute: 0, collapsed: true, isHourEnd: true });
    hours.push({
      hour,
      label: padTime(hour),
      hasOrders,
      startRow,
      endRow: rows.length,
      marks: [],
    });
  }

  return { hours, rows };
};

const findStartRow = (rows, totalMinutes) => {
  const hour = Math.floor(totalMinutes / 60);
  const minute = snapToSlot(totalMinutes % 60);

  const exactIndex = rows.findIndex(
    (row) => row.hour === hour && (row.collapsed || row.minute === minute),
  );
  if (exactIndex >= 0) return exactIndex;

  const hourIndex = rows.findIndex((row) => row.hour === hour);
  return hourIndex >= 0 ? hourIndex : 0;
};

const findEndRow = (rows, totalMinutes) => {
  const index = rows.findIndex((row) => getRowStartMinutes(row) >= totalMinutes);
  return index >= 0 ? index : rows.length;
};

export const buildColumnCells = (orders = [], rows = []) => {
  if (!rows.length) return [];

  const occupied = new Set();
  const cells = [];

  const visibleStart = getRowStartMinutes(rows[0]);

  orders.forEach((order) => {
    const planned = getPlannedMinutes(order);
    const start = planned == null ? visibleStart : snapToSlot(planned);
    const end = start + ceilToSlotMinutes(getExecutionMinutes(order));
    const isOverdue = end <= visibleStart;
    const cellStart = isOverdue ? visibleStart : Math.max(start, visibleStart);
    const cellEnd = isOverdue ? visibleStart + SLOT_MINUTES : end;

    const startRow = findStartRow(rows, cellStart);
    const endRow = Math.max(startRow + 1, findEndRow(rows, cellEnd));

    cells.push({
      order,
      startRow,
      endRow,
    });

    for (let row = startRow; row < endRow; row += 1) {
      occupied.add(row);
    }
  });

  for (let row = 0; row < rows.length; row += 1) {
    if (occupied.has(row)) continue;

    let endRow = row + 1;
    while (
      endRow < rows.length &&
      !occupied.has(endRow) &&
      rows[endRow].hour === rows[row].hour
    ) {
      endRow += 1;
    }

    cells.push({
      order: null,
      startRow: row,
      endRow,
    });
    row = endRow - 1;
  }

  return cells.sort((first, second) => first.startRow - second.startRow);
};

export const isHourEndRow = (rows, endRow) => {
  if (!rows.length || endRow <= 0) return false;
  if (endRow >= rows.length) return true;

  return rows[endRow].hour !== rows[endRow - 1].hour;
};
